import React, { useEffect, useState } from "react";
import axios from "axios";
import sharedStyles from "../styles/shared.module.css";
import adminStyles from "../styles/adminPostagens.module.css";
import { Check, X } from "lucide-react";
import Lightbox, { Slide } from "yet-another-react-lightbox";
import Video from "yet-another-react-lightbox/plugins/video";
import "yet-another-react-lightbox/styles.css";

type Tab = "pendentes" | "todas";

type PostagemAdminType = {
    id: number;
    conteudo: string;
    usuarioNome: string;
    status: "PENDENTE" | "APROVADA" | "REPROVADA";
    arquivos: {
        url: string;
        tipo?: "video" | "image";
        pontoInteresse?: string | null;
        pontoImportante?: string | null;
    }[];
};

const AdminPostagens = () => {
    const [postagens, setPostagens] = useState<PostagemAdminType[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<Tab>("pendentes");
    const [openDropdowns, setOpenDropdowns] = useState<number[]>([]);

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [lightboxSlides, setLightboxSlides] = useState<Slide[]>([]);

    const BASE_URL = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token") || "";

    const fetchPostagens = async (tab: Tab) => {
        setLoading(true);
        setError(null);
        try {
            const url =
                tab === "pendentes"
                    ? `${BASE_URL}/admins/postagens/pendentes`
                    : `${BASE_URL}/admins/postagens`;

            const res = await axios.get<PostagemAdminType[]>(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            console.log("Postagens recebidas do backend:", res.data); // <-- aqui
            setPostagens(res.data);
        } catch (err) {
            console.error(err);
            setError("Erro ao buscar postagens");
        } finally {
            setLoading(false);
        }
    };

    const alterarStatus = async (id: number, status: "APROVADA" | "REPROVADA") => {
        try {
            await axios.patch(
                `${BASE_URL}/admins/postagens/${id}/status`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { status },
                }
            );
            fetchPostagens(activeTab);
        } catch (err) {
            console.error(err);
            setError("Erro ao atualizar status da postagem");
        }
    };

    useEffect(() => {
        fetchPostagens(activeTab);
    }, [activeTab]);

    const toggleDropdown = (id: number) => {
        setOpenDropdowns((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const openLightbox = (arquivos: PostagemAdminType["arquivos"], index: number) => {
        const slides: Slide[] = arquivos.map((a) => {
            // Tentativa de vídeo primeiro
            return {
                type: "video",
                sources: [{ src: `${BASE_URL}${a.url}`, type: "video/mp4" }],
                poster: `${BASE_URL}${a.url}`,
                onError: () => {
                    // se falhar, converte para imagem
                    return { src: `${BASE_URL}${a.url}` };
                },
            };
        });
        setLightboxSlides(slides);
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    if (loading) return <p>Carregando postagens...</p>;
    if (error) return <p>{error}</p>;

    return (
        <div className={sharedStyles.container}>
            <h1>Postagens</h1>

            {/* Tabs */}
            <div className={adminStyles.tabs}>
                <button
                    className={`${adminStyles.tabButton} ${activeTab === "pendentes" ? adminStyles.activeTab : ""
                        }`}
                    onClick={() => setActiveTab("pendentes")}
                >
                    Pendentes
                </button>
                <button
                    className={`${adminStyles.tabButton} ${activeTab === "todas" ? adminStyles.activeTab : ""
                        }`}
                    onClick={() => setActiveTab("todas")}
                >
                    Todas
                </button>
            </div>

            {/* Lista */}
            {postagens.length === 0 ? (
                <p>Sem postagens encontradas</p>
            ) : (
                <div className={adminStyles.list}>
                    {postagens.map((postagem) => {
                        const isOpen = openDropdowns.includes(postagem.id);
                        return (
                            <div key={postagem.id} className={adminStyles.card}>
                                <div
                                    className={`${adminStyles.dropdownHeader} ${isOpen ? adminStyles.open : ""
                                        }`}
                                    onClick={() => toggleDropdown(postagem.id)}
                                >
                                    <span className={adminStyles.dropdownTitle}>
                                        <strong>Postagem {postagem.id}</strong>
                                    </span>
                                    <span className={adminStyles.dropdownIcon}>{isOpen ? "▲" : "▼"}</span>
                                </div>

                                {isOpen && (
                                    <div className={adminStyles.dropdownContent}>
                                        <div className={adminStyles.cardInfo}>
                                            <div className={adminStyles.infoRow}>
                                                <strong>Usuário:</strong> {postagem.usuarioNome}
                                            </div>
                                            <div className={adminStyles.infoRow}>
                                                <strong>Conteúdo:</strong> {postagem.conteudo || "—"}
                                            </div>
                                            <div className={adminStyles.infoRow}>
                                                <strong>Status:</strong>{" "}
                                                <span
                                                    className={`${adminStyles.statusBadge} ${postagem.status === "PENDENTE"
                                                        ? adminStyles.pending
                                                        : postagem.status === "APROVADA"
                                                            ? adminStyles.approved
                                                            : adminStyles.rejected
                                                        }`}
                                                >
                                                    {postagem.status}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Miniaturas */}
                                        {postagem.arquivos.length > 0 && (
                                            <div className={adminStyles.fotos}>
                                                {postagem.arquivos.map((a, idx) => {
                                                    const urlCompleta = `${BASE_URL}${a.url}`;
                                                    return (
                                                        <ArquivoThumb
                                                            key={idx}
                                                            arquivo={a}
                                                            urlCompleta={urlCompleta}
                                                            onClick={() => openLightbox(postagem.arquivos, idx)}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}


                                        {/* Botões */}
                                        <div className={adminStyles.actions}>
                                            {(postagem.status === "PENDENTE" || postagem.status === "REPROVADA") && (
                                                <button
                                                    className={adminStyles.aprovarButton}
                                                    onClick={() => alterarStatus(postagem.id, "APROVADA")}
                                                >
                                                    <Check size={16} style={{ marginRight: "6px" }} />
                                                    Aprovar
                                                </button>
                                            )}
                                            {(postagem.status === "PENDENTE" || postagem.status === "APROVADA") && (
                                                <button
                                                    className={adminStyles.rejeitarButton}
                                                    onClick={() => alterarStatus(postagem.id, "REPROVADA")}
                                                >
                                                    <X size={16} style={{ marginRight: "6px" }} />
                                                    Reprovar
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <Lightbox
                open={lightboxOpen}
                index={lightboxIndex}
                close={() => setLightboxOpen(false)}
                slides={lightboxSlides}
                plugins={[Video]}
            />
        </div>
    );
};

export default AdminPostagens;

type ArquivoThumbProps = {
    arquivo: PostagemAdminType["arquivos"][0];
    urlCompleta: string;
    onClick: () => void;
};

const ArquivoThumb: React.FC<ArquivoThumbProps> = ({ arquivo, urlCompleta, onClick }) => {
    const [isVideo, setIsVideo] = useState(true); // assume que é vídeo até falhar

    return (
        <div
            style={{
                display: "inline-block",
                margin: "4px",
                cursor: "pointer",
                position: "relative",
            }}
            onClick={onClick}
        >
            {isVideo ? (
                <video
                    src={urlCompleta}
                    muted
                    loop
                    style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: "8px",
                    }}
                    onError={() => setIsVideo(false)} // fallback -> imagem
                />
            ) : (
                <img
                    src={urlCompleta}
                    alt={arquivo.pontoInteresse || ""}
                    style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: "8px",
                    }}
                />
            )}

            {/* Badge só se realmente for vídeo */}
            {isVideo && (
                <div
                    style={{
                        position: "absolute",
                        top: "6px",
                        left: "6px",
                        background: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        fontSize: "10px",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontWeight: "bold",
                        pointerEvents: "none",
                    }}
                >
                    VIDEO
                </div>
            )}

            {/* Pontos */}
            {(arquivo.pontoInteresse || arquivo.pontoImportante) && (
                <div style={{ fontSize: "12px", marginTop: "2px" }}>
                    {arquivo.pontoInteresse && <div>📍 {arquivo.pontoInteresse}</div>}
                    {arquivo.pontoImportante && <div>❗ {arquivo.pontoImportante}</div>}
                </div>
            )}
        </div>
    );
};
